// Shared 360 video recording system (extracted from NewVideo.js) so it can be
// reused by Video.js, StencilVideo.js and IntegratedVideo.js.
//
// Usage:
//   startCarVideoRecording({ config, clientId, videoEl, inspectionId, token, isIos })
//     -> call when the camera starts (after videoEl.srcObject is set)
//   stopCarVideoRecordingAndUpload()  -> call when the camera stops normally (uploads)
//   cancelCarVideoRecording()         -> call when leaving/aborting (no upload)
//
// Recording uses a layered fallback so it works across ALL OS / Android / browser
// versions (including very old ones) while keeping BOTH the live preview and the
// recording smooth. Root causes each layer answers (all seen live on devices):
//   - recording via a canvas judders: the draw loop shares the JS main thread
//     with the ML inference, which starves it;
//   - recording the camera at native 1080p lags the PREVIEW (VP8) or everything
//     (webm;h264): the encoders are software and 1080p@30 saturates the CPU.
//     The proven-sustainable encode budget is ~720p@30 pixels/sec;
//   - applyConstraints on a clone cannot be trusted: devices reject rescaling,
//     or resolve the promise WITHOUT rescaling (getSettings stays 1920x1080).
// Paths (the no-data watchdog advances to the next path if a recorder emits
// nothing within 2.5s). Android order: 1 -> 2 -> 3 -> 4. iOS order: 4 -> 2 -> 3
// (canvas FIRST so the recording is 480p@10 WITHOUT sending constraints to the
// camera -- Safari can reconfigure the shared source and degrade the preview
// and uploaded images; the clone remains the old-Safari 0-chunk fallback):
//   1. Worker-scaled clone (Android, Chromium 94+): pipe the cloned camera track
//      through a Web Worker that rescales every frame to recordingHeight (and
//      drops frames to recordingFps; defaults 480p@10 for low-end headroom) and
//      record the generated track. Deterministic pixel control fully OFF the
//      main thread -> smooth recording AND smooth preview, independent of
//      whether the device's track adapter honors constraints.
//   2. Plain camera-track clone with VERIFIED constraints (downscale, else fps
//      cap; verified via getSettings, never trusted from the promise). On
//      Android, if nothing can be verified <= the safe budget, skip to canvas
//      (preview usability wins). iOS records the clone unconstrained: its
//      MediaRecorder is always hardware H.264, so native res is cheap.
//   3. <video> element captureStream where track cloning is unavailable.
//   4. Canvas recorder (very old browsers): smooth preview, recording may judder
//      under heavy ML -- but recording still works.
//   5. No MediaRecorder at all (iOS < 14.3): skip cleanly, inspection continues.
// Android avoids MP4 entirely (advertises support but yields 0 chunks from
// canvas AND cloned camera tracks) and prefers VP8 (predictable cost; webm;h264
// janked everything at 1080p on the test device). Only active when
// config.recordVideo === true. Bitrate cap bounds file size. Auto stops +
// uploads at 50 MB.

import {
  reserveVideoUpload,
  releaseReservedVideo,
  uploadReservedVideo,
  patLogs,
} from "../context/utils";

let mediaRecorder = null;
let recordedChunks = [];
let recordingCanvas = null;
let recordingStream = null;
// Source camera-track clone feeding the worker scaler (not part of
// recordingStream, so it must be stopped separately on teardown).
let recordingSourceTrack = null;
let scaleWorker = null;
let recordingDrawTimer = null;
let recordingDrawActive = false;
let recordedMimeType = "video/webm";
let recordingDataCheckTimer = null;
let receivedRecordingData = false;
let recordingBitrateValue = 800000;
let recordedBytes = 0;
let maxRecordBytes = 50 * 1024 * 1024; // auto stop + upload at 50 MB
let uploadWatchdog = null;
let ctx = {}; // { config, clientId, videoEl, inspectionId, token, isIos }
// Bumped on every start/stop/cancel so an async recorder start still pending
// (e.g. waiting for clone downscale constraints) can detect it went stale.
let recordingSession = 0;

// Browser-console logging (visible in DevTools) so recording issues can be
// diagnosed live during a device test.
const clog = (...args) => {
  try {
    console.log("[carVideoRecorder]", ...args);
  } catch (e) {}
};

const isVideoRecordingEnabled = () =>
  ctx.config &&
  Object.keys(ctx.config).includes("recordVideo") &&
  (ctx.config["recordVideo"] === true || ctx.config["recordVideo"] === "true");

const getSupportedVideoMime = (isIos, forCanvas) => {
  // iOS MediaRecorder is always hardware H.264 -> MP4 first there.
  // Android: NO MP4 at all (advertises support but yields 0 chunks from canvas
  // AND from cloned camera tracks -- seen live). VP8 first for camera streams
  // too: webm;h264 was tried and on the test device encoded so slowly at 1080p
  // that BOTH the preview and the recording janked (software/slow path), while
  // VP8's software cost is predictable and gets bounded by the downscale or
  // fps cap. webm;h264 stays only as the last-resort candidate.
  const candidates = isIos
    ? [
        "video/mp4;codecs=avc1.42E01E",
        "video/mp4;codecs=avc1",
        "video/mp4;codecs=h264",
        "video/mp4",
        "video/webm;codecs=vp8",
        "video/webm",
      ]
    : forCanvas
      ? ["video/webm;codecs=vp8", "video/webm;codecs=vp9", "video/webm"]
      : [
          "video/webm;codecs=vp8",
          "video/webm;codecs=vp9",
          "video/webm",
          "video/webm;codecs=h264",
        ];
  for (let i = 0; i < candidates.length; i++) {
    if (
      typeof MediaRecorder !== "undefined" &&
      typeof MediaRecorder.isTypeSupported === "function" &&
      MediaRecorder.isTypeSupported(candidates[i])
    ) {
      return candidates[i];
    }
  }
  return "";
};

const stopRecordingDrawLoop = () => {
  recordingDrawActive = false;
  if (recordingDrawTimer) {
    clearInterval(recordingDrawTimer);
    recordingDrawTimer = null;
  }
};

// Tear down any in-progress recorder before (re)starting on a different path.
// Detaches handlers first so stopping never triggers a spurious upload.
const teardownRecorder = () => {
  stopRecordingDrawLoop();
  if (mediaRecorder) {
    mediaRecorder.ondataavailable = null;
    mediaRecorder.onstop = null;
    try {
      if (mediaRecorder.state !== "inactive") mediaRecorder.stop();
    } catch (e) {}
  }
  if (recordingSourceTrack) {
    try {
      recordingSourceTrack.stop();
    } catch (e) {}
    recordingSourceTrack = null;
  }
  if (recordingStream) {
    try {
      recordingStream.getTracks().forEach((t) => t.stop());
    } catch (e) {}
    recordingStream = null;
  }
  recordingCanvas = null;
};

const clearUploadWatchdog = () => {
  if (uploadWatchdog) {
    clearTimeout(uploadWatchdog);
    uploadWatchdog = null;
  }
};

// Stop + upload when the recording reaches the size cap (the inspection keeps
// running; recording just stops growing so the file never exceeds the cap).
const trackRecordedSize = (size) => {
  recordedBytes += size;
  if (recordedBytes >= maxRecordBytes) {
    // patLogs(
    //   `recording hit ${(recordedBytes / 1048576).toFixed(
    //     1,
    //   )}MB cap; stopping and uploading`,
    //   ctx.inspectionId,
    // );
    stopCarVideoRecordingAndUpload();
  }
};

// Create + start the MediaRecorder on the current recordingStream. Shared by
// every recording path (they differ only in how recordingStream is produced).
const beginRecorderOnStream = (recordBitrate, label, forCanvas) => {
  recordedChunks = [];
  recordedBytes = 0;
  receivedRecordingData = false;
  const mimeType = getSupportedVideoMime(ctx.isIos, !!forCanvas);
  const options = {
    videoBitsPerSecond: recordBitrate || recordingBitrateValue,
  };
  if (mimeType) options.mimeType = mimeType;
  mediaRecorder = new MediaRecorder(recordingStream, options);
  recordedMimeType =
    mediaRecorder.mimeType && mediaRecorder.mimeType.length > 0
      ? mediaRecorder.mimeType
      : mimeType || "video/webm";
  mediaRecorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) {
      receivedRecordingData = true;
      if (recordingDataCheckTimer) {
        clearTimeout(recordingDataCheckTimer);
        recordingDataCheckTimer = null;
      }
      recordedChunks.push(e.data);
      clog(label + " chunk", e.data.size, "bytes; total", recordedBytes);
      trackRecordedSize(e.data.size);
    }
  };
  mediaRecorder.onstop = () => {
    uploadRecordedVideoBlob();
  };
  mediaRecorder.start(1000);
};

// Worker source: rescales every camera VideoFrame to the target height,
// entirely off the main thread, and feeds the generated track. Primary scale
// via createImageBitmap({resize*}) (more reliable than drawImage(VideoFrame)
// on some Chrome versions), OffscreenCanvas as inner fallback. Reports its
// first frame / first error back to the page so device logs show WHY the path
// worked or died instead of failing silently.
const SCALE_WORKER_SRC = `
self.onmessage = (e) => {
  const { readable, writable, height, fps } = e.data;
  const minGapUs = fps > 0 ? Math.floor(1e6 / fps) : 0;
  let lastKeptTs = -1;
  let canvas = null, c2d = null, reportedStart = false, reportedErr = false;
  const scaleViaBitmap = async (frame, w) => {
    const bmp = await createImageBitmap(frame, {
      resizeWidth: w,
      resizeHeight: height,
      resizeQuality: "low",
    });
    const out = new VideoFrame(bmp, { timestamp: frame.timestamp });
    bmp.close();
    return out;
  };
  const scaleViaCanvas = (frame, w) => {
    if (!canvas || canvas.width !== w || canvas.height !== height) {
      canvas = new OffscreenCanvas(w, height);
      c2d = canvas.getContext("2d");
    }
    c2d.drawImage(frame, 0, 0, w, height);
    return new VideoFrame(canvas, { timestamp: frame.timestamp });
  };
  const transformer = new TransformStream({
    async transform(frame, controller) {
      try {
        // Optional fps cap: drop frames closer together than the target gap
        // (relieves scaling + encoding load on weak devices).
        if (minGapUs && typeof frame.timestamp === "number") {
          if (lastKeptTs >= 0 && frame.timestamp - lastKeptTs < minGapUs) {
            frame.close();
            return;
          }
          lastKeptTs = frame.timestamp;
        }
        const w = Math.max(
          2,
          Math.round((height * frame.displayWidth) / frame.displayHeight / 2) * 2
        );
        let scaled;
        try {
          scaled = await scaleViaBitmap(frame, w);
        } catch (e1) {
          scaled = scaleViaCanvas(frame, w);
        }
        frame.close();
        controller.enqueue(scaled);
        if (!reportedStart) {
          reportedStart = true;
          self.postMessage({ type: "scaling", width: w, height: height });
        }
      } catch (err) {
        try { frame.close(); } catch (e2) {}
        if (!reportedErr) {
          reportedErr = true;
          self.postMessage({ type: "error", message: String(err && err.message) });
        }
      }
    },
  });
  readable.pipeThrough(transformer).pipeTo(writable).catch((err) => {
    self.postMessage({ type: "pipe-ended", message: String(err && err.message) });
  });
};
`;

// Path 1 (Android, Chromium 94+): deterministic off-main-thread downscale.
// Clone the camera track, rescale every frame to recordHeight in a Web Worker
// (MediaStreamTrackProcessor -> OffscreenCanvas -> MediaStreamTrackGenerator),
// and record the generated 720p track. Doesn't depend on applyConstraints being
// honored (device adapters lie -- seen live) and never touches the preview
// track or the main thread. Returns true if engaged.
const startWorkerScaledRecording = (recordBitrate, recordHeight, recordFps) => {
  try {
    if (ctx.isIos) return false; // iOS lacks these APIs; its clone path is fine
    if (
      typeof MediaStreamTrackProcessor === "undefined" ||
      typeof MediaStreamTrackGenerator === "undefined" ||
      typeof OffscreenCanvas === "undefined" ||
      typeof Worker === "undefined" ||
      typeof MediaStream === "undefined"
    ) {
      clog("worker-scaled path unsupported (missing insertable-streams APIs)");
      return false;
    }
    if (!window.stream || !window.stream.getVideoTracks) return false;
    const srcTrack = window.stream.getVideoTracks()[0];
    if (!srcTrack || typeof srcTrack.clone !== "function") return false;

    teardownRecorder();
    const clonedTrack = srcTrack.clone();
    recordingSourceTrack = clonedTrack;
    const processor = new MediaStreamTrackProcessor({ track: clonedTrack });
    const generator = new MediaStreamTrackGenerator({ kind: "video" });
    if (!scaleWorker) {
      scaleWorker = new Worker(
        URL.createObjectURL(
          new Blob([SCALE_WORKER_SRC], { type: "application/javascript" }),
        ),
      );
      // Diagnostics back from the worker so device logs show what happened.
      scaleWorker.onmessage = (e) => {
        const d = e.data || {};
        clog(
          "scale worker:",
          d.type,
          d.message || `${d.width || ""}x${d.height || ""}`,
        );
      };
      // A CSP that blocks blob: workers surfaces here (silent 0-frame death
      // otherwise -- seen live).
      scaleWorker.onerror = (e) => {
        clog(
          "scale worker ERROR (possibly CSP blocking blob worker):",
          e && e.message,
        );
      };
    }
    scaleWorker.postMessage(
      {
        readable: processor.readable,
        writable: generator.writable,
        height: recordHeight,
        fps: recordFps || 0, // 0 => keep the camera's native rate
      },
      [processor.readable, generator.writable],
    );
    recordingStream = new MediaStream([generator]);
    beginRecorderOnStream(recordBitrate, "worker-scaled", false);
    clog("worker-scaled recording started", {
      mimeType: recordedMimeType,
      recordHeight,
      recordFps: recordFps || "native",
    });
    // patLogs(
    //   `recording via worker-scaled clone ${recordHeight}p (off-main-thread)`,
    //   ctx.inspectionId,
    // );
    return true;
  } catch (err) {
    clog("worker-scaled recording FAILED", err && err.message);
    //patLogs(`worker-scaled recording failed: ${err.message}`, ctx.inspectionId);
    try {
      teardownRecorder();
    } catch (e) {}
    return false;
  }
};

// Path 2: record a plain CLONE of the camera track with VERIFIED constraints.
// Encoding runs off the main thread -> smooth recording immune to ML jank. The
// LIVE PREVIEW stays smooth only if the encoder's pixels/sec is bounded (the
// proven budget is ~720p@30; native 1080p@30 melts weak devices): try a binding
// downscale, else an fps cap -- and VERIFY each via getSettings, never trusting
// the promise (devices resolve it without rescaling -- seen live). On Android,
// if nothing verifies, hand off to the canvas recorder: preview usability wins
// over recording smoothness. iOS records unconstrained (always hardware H.264).
// Returns true if the path engaged, false if unsupported (caller falls back).
const startDirectStreamRecording = (
  recordBitrate,
  recordHeight,
  recordFps,
) => {
  try {
    teardownRecorder();
    if (!window.stream) return false;
    const srcTrack =
      window.stream.getVideoTracks && window.stream.getVideoTracks()[0]
        ? window.stream.getVideoTracks()[0]
        : null;
    if (!srcTrack) return false;
    // A cloned, independent track is required: without clone() / the MediaStream
    // constructor (very old browsers) we can't record without risking the shared
    // preview track being stopped on teardown -> bail to the next path.
    if (
      typeof MediaStream === "undefined" ||
      typeof srcTrack.clone !== "function"
    ) {
      clog("direct-stream unsupported (no clone/MediaStream ctor)");
      return false;
    }
    const session = recordingSession;
    const clonedTrack = srcTrack.clone();
    const stream = new MediaStream([clonedTrack]);
    recordingStream = stream;

    const settingsOf = () => {
      try {
        return (clonedTrack.getSettings && clonedTrack.getSettings()) || {};
      } catch (e) {
        return {};
      }
    };

    // Constrain ONLY the clone (Android; skipped on iOS). Stage 1: binding
    // downscale to recordHeight. Stage 2: cap frame rate to 15 (decimation is
    // adapter-supported even where rescaling isn't; 1080p@15 costs about the
    // same pixels/sec as the proven 720p@30). Each stage is VERIFIED via
    // getSettings before being believed.
    let constraintsSettled = Promise.resolve("ios-native");
    if (!ctx.isIos) {
      if (typeof clonedTrack.applyConstraints === "function") {
        const targetW = Math.round((recordHeight * 16) / 9);
        constraintsSettled = clonedTrack
          .applyConstraints({
            width: { ideal: targetW, max: targetW },
            height: { ideal: recordHeight, max: recordHeight },
            frameRate: { ideal: recordFps || 30, max: recordFps || 30 },
          })
          .catch((e1) => {
            clog(
              "clone downscale rejected",
              e1 && e1.name,
              e1 && e1.constraint,
              e1 && e1.message,
            );
          })
          .then(() => {
            const s = settingsOf();
            if (s.height && s.height <= recordHeight * 1.1) return "downscaled";
            clog("downscale not verified by getSettings", s.width, s.height);
            return clonedTrack
              .applyConstraints({ frameRate: { ideal: 15, max: 15 } })
              .catch((e2) => {
                clog("clone fps cap rejected", e2 && e2.name, e2 && e2.message);
              })
              .then(() => {
                const s2 = settingsOf();
                if (s2.frameRate && s2.frameRate <= 16) return "fps-capped";
                clog("fps cap not verified by getSettings", s2.frameRate);
                return "unverified";
              });
          });
      } else {
        constraintsSettled = Promise.resolve("unverified");
      }
    }

    // Start the recorder once the constraints have settled so the encoder never
    // sees a mid-stream resolution/rate switch.
    const startRecorder = (constraintMode) => {
      try {
        // Stale: stopped/cancelled or switched paths while constraints applied.
        if (session !== recordingSession || recordingStream !== stream) return;
        // Android with nothing verified would encode native 1080p@30 --
        // proven to lag the preview (or everything) on weak devices. Preview
        // usability wins: use the canvas recorder instead (smooth preview;
        // recording may judder under ML -- the accepted floor).
        if (constraintMode === "unverified") {
          clog("clone constraints unverified -> canvas recorder (preview-first)");
          // patLogs(
          //   "clone constraints unverified; using canvas recorder",
          //   ctx.inspectionId,
          // );
          startCanvasRecording(recordBitrate, recordHeight, recordFps);
          return;
        }
        beginRecorderOnStream(recordBitrate, "direct", false);
        const s = settingsOf();
        clog("direct-stream recording started", {
          mimeType: recordedMimeType,
          constraintMode,
          width: s.width,
          height: s.height,
          fps: s.frameRate,
        });
        // patLogs(
        //   `recording camera clone (${constraintMode}) ${s.width || "?"}x${
        //     s.height || "?"
        //   }@${s.frameRate || "?"}`,
        //   ctx.inspectionId,
        // );
      } catch (err) {
        // No chunks will arrive -> the no-data watchdog advances the ladder.
        clog("direct recorder start FAILED", err && err.message);
        // patLogs(
        //   `direct stream recording failed: ${err.message}`,
        //   ctx.inspectionId,
        // );
      }
    };
    constraintsSettled.then(startRecorder, () => startRecorder("unverified"));
    return true;
  } catch (err) {
    clog("direct stream recording FAILED", err && err.message);
    patLogs(`direct stream recording failed: ${err.message}`, ctx.inspectionId);
    return false;
  }
};

// Path 3: record the <video> element's own output via captureStream (used when
// track cloning is unavailable). Still encodes off the main thread -> smooth
// recording; captures at the element's native resolution. Unsupported on
// Safari/iOS -> returns false so the caller falls back further.
const startElementStreamRecording = (recordBitrate) => {
  try {
    teardownRecorder();
    const vw = ctx.videoEl;
    if (!vw) return false;
    const capture =
      typeof vw.captureStream === "function"
        ? vw.captureStream.bind(vw)
        : typeof vw.mozCaptureStream === "function"
          ? vw.mozCaptureStream.bind(vw)
          : null;
    if (!capture) {
      clog("element captureStream unsupported on this browser");
      return false;
    }
    const stream = capture();
    if (
      !stream ||
      !stream.getVideoTracks ||
      stream.getVideoTracks().length === 0
    ) {
      clog("element captureStream produced no video track");
      return false;
    }
    recordingStream = stream;
    beginRecorderOnStream(recordBitrate, "element", false);
    clog("element-captureStream recording started", {
      mimeType: recordedMimeType,
    });
    // patLogs(
    //   "recording via <video> captureStream (off-thread encode)",
    //   ctx.inspectionId,
    // );
    return true;
  } catch (err) {
    clog("element stream recording FAILED", err && err.message);
    patLogs(`element stream recording failed: ${err.message}`, ctx.inspectionId);
    return false;
  }
};

// Path 4: draw the camera into a dedicated off-screen canvas and record that
// canvas stream. Used when no stream-capture path works (or none can be
// verified safe) so recording still happens everywhere. The recorded video can
// judder under heavy ML (the canvas draw runs on the main thread), but the live
// preview is never disturbed.
const startCanvasRecording = (recordBitrate, recordHeight, recordFps) => {
  try {
    teardownRecorder();
    const vw = ctx.videoEl;
    if (!vw) return false;

    recordingCanvas = document.createElement("canvas");
    recordingCanvas.width = Math.round((recordHeight * 16) / 9);
    recordingCanvas.height = recordHeight;
    const rctx = recordingCanvas.getContext("2d");

    const drawRecordingFrame = () => {
      try {
        if (!vw.videoWidth || !vw.videoHeight) return;
        const targetH = recordHeight;
        const targetW = Math.round(targetH * (vw.videoWidth / vw.videoHeight));
        if (recordingCanvas.width !== targetW) recordingCanvas.width = targetW;
        if (recordingCanvas.height !== targetH)
          recordingCanvas.height = targetH;
        rctx.drawImage(vw, 0, 0, targetW, targetH);
      } catch (e) {}
    };

    // Draw with a plain setInterval so the canvas is updated even when the video
    // doesn't fire requestVideoFrameCallback (that produced 0 frames on some
    // Android devices). Draw once immediately so captureStream has a painted
    // canvas before recording starts.
    recordingDrawActive = true;
    let nativeFps = 30;
    try {
      const camTrack =
        window.stream && window.stream.getVideoTracks
          ? window.stream.getVideoTracks()[0]
          : null;
      const s = camTrack && camTrack.getSettings ? camTrack.getSettings() : null;
      if (s && s.frameRate) nativeFps = Math.max(1, Math.round(s.frameRate));
    } catch (e) {}
    const drawFps = recordFps || nativeFps;
    clog("draw fps", drawFps, "(native ~", nativeFps, ")");
    let drewOnce = false;
    let loggedNoDim = false;
    const drawTick = () => {
      if (!recordingDrawActive) return;
      if (!vw.videoWidth || !vw.videoHeight) {
        if (!loggedNoDim) {
          loggedNoDim = true;
          clog("draw waiting: video has no dimensions yet (videoWidth=0)");
        }
        return;
      }
      drawRecordingFrame();
      if (!drewOnce) {
        drewOnce = true;
        clog("first canvas draw ok", {
          videoWidth: vw.videoWidth,
          videoHeight: vw.videoHeight,
        });
      }
    };
    drawTick();
    recordingDrawTimer = setInterval(drawTick, Math.round(1000 / drawFps));

    if (typeof recordingCanvas.captureStream !== "function") {
      clog("canvas.captureStream unsupported -> cannot record on this device");
      // patLogs(
      //   "video recording unsupported on this browser (no canvas.captureStream)",
      //   ctx.inspectionId,
      // );
      stopRecordingDrawLoop();
      recordingCanvas = null;
      return false;
    }
    recordingStream = recordingCanvas.captureStream(drawFps);
    beginRecorderOnStream(recordBitrate, "canvas", true);
    clog("canvas recording started", {
      recordHeight,
      recordFps,
      recordBitrate,
      mimeType: recordedMimeType,
    });
    // patLogs(
    //   `${recordHeight}p canvas video recording started`,
    //   ctx.inspectionId,
    // );
    return true;
  } catch (err) {
    clog("canvas recording FAILED", err && err.message);
    patLogs(`canvas recording failed: ${err.message}`, ctx.inspectionId);
    return false;
  }
};

export const startCarVideoRecording = (opts) => {
  try {
    ctx = opts || {};
    recordingSession++;
    clog("startCarVideoRecording called", {
      recordVideo: ctx.config && ctx.config["recordVideo"],
      isIos: ctx.isIos,
      hasVideoEl: !!ctx.videoEl,
      inspectionId: ctx.inspectionId,
    });
    // patLogs(
    //   `video recording: recordVideo flag = ${JSON.stringify(
    //     ctx.config && ctx.config["recordVideo"],
    //   )}`,
    //   ctx.inspectionId,
    // );
    if (!isVideoRecordingEnabled()) {
      clog("SKIPPED: recordVideo not true in loaded config");
      // patLogs(
      //   "video recording skipped: recordVideo not true in loaded config",
      //   ctx.inspectionId,
      // );
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      clog("SKIPPED: MediaRecorder not supported");
      // patLogs(
      //   "video recording skipped: MediaRecorder not supported",
      //   ctx.inspectionId,
      // );
      return;
    }
    const vw = ctx.videoEl;
    if (!vw) {
      clog("SKIPPED: no video element");
      //patLogs("video recording skipped: no video element", ctx.inspectionId);
      return;
    }
    const config = ctx.config;

    // Recording geometry (config can override): the worker/clone paths scale to
    // this height; the canvas fallback draws at it. Defaults are 480p@10 --
    // ~4.1M pixels/s into the encoder, ~6.7x lighter than 720p@30 and ~15x
    // lighter than native 1080p@30, so recording stays smooth on low-end
    // Android too (480p was also this app's original Android recording spec;
    // 10fps trades visible smoothness for extra headroom).
    // Raise per client via recordingHeight / recordingFps for strong devices.
    const recordHeight = Object.keys(config["video-page"]).includes(
      "recordingHeight",
    )
      ? config["video-page"]["recordingHeight"]
      : 480;
    const recordFps = Object.keys(config["video-page"]).includes("recordingFps")
      ? config["video-page"]["recordingFps"]
      : 10;
    // Recording bitrate (config can override). Default 0.6 Mbps on both
    // platforms -- comfortable quality at 480p@10, ~4.5MB/min uploads.
    const recordBitrate = Object.keys(config["video-page"]).includes(
      "recordingBitrate",
    )
      ? config["video-page"]["recordingBitrate"]
      : 600000;
    recordingBitrateValue = recordBitrate;
    receivedRecordingData = false;
    recordedBytes = 0;
    maxRecordBytes =
      (Object.keys(config["video-page"]).includes("recordingMaxSizeMB")
        ? config["video-page"]["recordingMaxSizeMB"]
        : 50) *
      1024 *
      1024;

    // Try stream paths most-capable first; the no-data watchdog advances to the
    // next path if the recorder emits nothing within 2.5s, ending at the canvas
    // recorder, so a video is produced on every device/browser version.
    // iOS: record 480p@recordFps via the off-screen canvas FIRST -- it reads
    // pixels from the <video> element and never sends constraints to the
    // camera, so the live preview and the uploaded damage images can never be
    // affected (Safari applying clone constraints can reconfigure the SHARED
    // camera source). If old-Safari canvas capture yields no data, the
    // watchdog falls back to the direct clone (native res, hardware H.264),
    // mirroring the app's original iOS design. Android keeps the worker-scaled
    // path first (its VP8 encoder is software; the canvas path judders there).
    const attempts = ctx.isIos
      ? [
          () =>
            startCanvasRecording(recordBitrate, recordHeight, recordFps),
          () =>
            startDirectStreamRecording(recordBitrate, recordHeight, recordFps),
          () => startElementStreamRecording(recordBitrate),
        ]
      : [
          () =>
            startWorkerScaledRecording(recordBitrate, recordHeight, recordFps),
          () =>
            startDirectStreamRecording(recordBitrate, recordHeight, recordFps),
          () => startElementStreamRecording(recordBitrate),
        ];
    let attemptIdx = 0;
    const armNoDataWatchdog = () => {
      if (recordingDataCheckTimer) clearTimeout(recordingDataCheckTimer);
      recordingDataCheckTimer = setTimeout(() => {
        recordingDataCheckTimer = null;
        if (receivedRecordingData) return;
        clog("no data in 2.5s -> advancing to next recording path");
        // patLogs(
        //   "recording path emitted no data; advancing to next path",
        //   ctx.inspectionId,
        // );
        tryNextPath();
      }, 2500);
    };
    const tryNextPath = () => {
      while (attemptIdx < attempts.length) {
        if (attempts[attemptIdx++]()) {
          armNoDataWatchdog();
          return;
        }
      }
      startCanvasRecording(recordBitrate, recordHeight, recordFps);
    };
    tryNextPath();
  } catch (err) {
    clog("startCarVideoRecording FAILED", err && err.message);
    patLogs(`video recording start failed: ${err.message}`, ctx.inspectionId);
  }
};

// Stop recording and queue the upload. Reserves the upload slot synchronously
// so the success page accounts for the video before navigation happens.
export const stopCarVideoRecordingAndUpload = () => {
  try {
    recordingSession++; // void any recorder start still pending
    if (recordingDataCheckTimer) {
      clearTimeout(recordingDataCheckTimer);
      recordingDataCheckTimer = null;
    }
    stopRecordingDrawLoop();
    clog(
      "stopCarVideoRecordingAndUpload called; recorder state =",
      mediaRecorder ? mediaRecorder.state : "none",
    );
    // patLogs(
    //   `video recording stop requested; recorder state = ${
    //     mediaRecorder ? mediaRecorder.state : "none"
    //   }`,
    //   ctx.inspectionId,
    // );
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      reserveVideoUpload();
      // Safety net: if onstop somehow never fires, force-release the reserved
      // slot so the success page is never blocked waiting on the video.
      clearUploadWatchdog();
      uploadWatchdog = setTimeout(() => {
        uploadWatchdog = null;
        clog("upload watchdog: onstop never fired; releasing slot");
        releaseReservedVideo();
      }, 20000);
      mediaRecorder.stop();
    }
  } catch (err) {
    patLogs(`video recording stop failed: ${err.message}`, ctx.inspectionId);
  }
};

const uploadRecordedVideoBlob = async () => {
  // onstop fired -> the upload flow (or no-chunks release) will run, and the XHR
  // has its own timeout, so the watchdog is no longer needed.
  clearUploadWatchdog();
  try {
    if (recordingSourceTrack) {
      recordingSourceTrack.stop();
      recordingSourceTrack = null;
    }
    if (recordingStream) {
      recordingStream.getTracks().forEach((t) => t.stop());
    }
  } catch (e) {}
  clog("uploadRecordedVideoBlob: chunks =", recordedChunks.length);
  let formData;
  try {
    if (!recordedChunks.length) {
      clog("UPLOAD SKIPPED: no chunks were recorded");
      // patLogs(
      //   "video upload skipped: no chunks were recorded",
      //   ctx.inspectionId,
      // );
      releaseReservedVideo();
      return;
    }
    const ext = recordedMimeType.includes("mp4") ? "mp4" : "webm";
    const blob = new Blob(recordedChunks, { type: recordedMimeType });
    formData = new FormData();
    formData.append("video", blob, `${ctx.inspectionId}_video.${ext}`);
    formData.append("token", ctx.token);
    formData.append("inspectionId", ctx.inspectionId);
    // patLogs(
    //   `uploading recorded video (${ext}) size ${(blob.size / 1024).toFixed(
    //     1,
    //   )}KB`,
    //   ctx.inspectionId,
    // );
  } catch (err) {
    patLogs(`recorded video prepare failed: ${err.message}`, ctx.inspectionId);
    releaseReservedVideo();
    return;
  }
  clog("POSTing video to /uploadvideo ...");
  const ok = await uploadReservedVideo(formData);
  clog("upload result:", ok ? "SUCCESS" : "FAILED (backend/network)");
  // patLogs(
  //   `video upload ${ok ? "succeeded" : "failed (backend/network)"}`,
  //   ctx.inspectionId,
  // );
};

// Discard recording without uploading (used when leaving/aborting the module).
export const cancelCarVideoRecording = () => {
  clearUploadWatchdog();
  recordingSession++; // void any recorder start still pending
  try {
    if (recordingDataCheckTimer) {
      clearTimeout(recordingDataCheckTimer);
      recordingDataCheckTimer = null;
    }
    stopRecordingDrawLoop();
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.ondataavailable = null;
      mediaRecorder.onstop = null;
      mediaRecorder.stop();
    }
    if (recordingSourceTrack) {
      recordingSourceTrack.stop();
      recordingSourceTrack = null;
    }
    if (recordingStream) {
      recordingStream.getTracks().forEach((t) => t.stop());
    }
  } catch (e) {}
  mediaRecorder = null;
  recordedChunks = [];
  recordingStream = null;
  recordingSourceTrack = null;
  recordingCanvas = null;
};
